import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';
import {
  getComplaintChannelsApi,
  getComplaintHandlersApi,
  updateComplaintChannelsApi,
  updateComplaintHandlersApi,
  type UpdateComplaintChannelsRequest,
  type UpdateComplaintHandlersRequest,
} from '@/lib/complaints-api';
import { listEmployeesApi } from '@/lib/employees-api';
import { maskEmployeeId } from '@/lib/complaints-model';
import type { AdminEmployeeListResult } from '@/lib/types';

const INPUT =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';

export function ComplaintConfigPanel() {
  const queryClient = useQueryClient();
  const handlersQuery = useQuery({
    queryKey: ['complaints', 'config', 'handlers'],
    queryFn: getComplaintHandlersApi,
  });
  const channelsQuery = useQuery({
    queryKey: ['complaints', 'config', 'channels'],
    queryFn: getComplaintChannelsApi,
  });
  const employeesQuery = useQuery({
    queryKey: ['complaints', 'config', 'active-employees'],
    queryFn: () =>
      listEmployeesApi({
        page: 1,
        pageSize: 100,
        status: 'ACTIVE',
        sortBy: 'name',
        sortOrder: 'asc',
      }),
  });

  const [complianceEmployeeId, setComplianceEmployeeId] = useState('');
  const [trainingEmployeeId, setTrainingEmployeeId] = useState('');
  const [enterpriseWechat, setEnterpriseWechat] = useState('');
  const [hotline, setHotline] = useState('');
  const [savingHandlers, setSavingHandlers] = useState(false);
  const [savingChannels, setSavingChannels] = useState(false);
  const [handlerMessage, setHandlerMessage] = useState<string | null>(null);
  const [channelMessage, setChannelMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!handlersQuery.data) return;
    setComplianceEmployeeId(handlersQuery.data.compliance?.employeeId ?? '');
    setTrainingEmployeeId(handlersQuery.data.training?.employeeId ?? '');
  }, [handlersQuery.data]);

  useEffect(() => {
    if (!channelsQuery.data) return;
    setEnterpriseWechat(channelsQuery.data.enterpriseWechat ?? '');
    setHotline(channelsQuery.data.hotline ?? '');
  }, [channelsQuery.data]);

  const employees = useMemo(
    () => employeesQuery.data?.items ?? [],
    [employeesQuery.data?.items],
  );

  async function saveHandlers(event: FormEvent) {
    event.preventDefault();
    setHandlerMessage(null);
    if (!complianceEmployeeId || !trainingEmployeeId) {
      setHandlerMessage('必须分别选择合规处理人与培训处理人。');
      return;
    }
    setSavingHandlers(true);
    try {
      const input: UpdateComplaintHandlersRequest = {
        complianceEmployeeId,
        trainingEmployeeId,
      };
      const next = await updateComplaintHandlersApi(input);
      queryClient.setQueryData(['complaints', 'config', 'handlers'], next);
      await queryClient.invalidateQueries({ queryKey: ['complaints', 'meta'] });
      setHandlerMessage('处理人配置已由后端保存并写入脱敏审计。');
    } catch (error) {
      setHandlerMessage(toConfigError(error));
    } finally {
      setSavingHandlers(false);
    }
  }

  async function saveChannels(event: FormEvent) {
    event.preventDefault();
    setChannelMessage(null);
    setSavingChannels(true);
    try {
      const input: UpdateComplaintChannelsRequest = {
        enterpriseWechat: enterpriseWechat.trim(),
        hotline: hotline.trim(),
      };
      const next = await updateComplaintChannelsApi(input);
      queryClient.setQueryData(['complaints', 'config', 'channels'], next);
      await queryClient.invalidateQueries({ queryKey: ['complaints', 'meta'] });
      setChannelMessage('受控渠道已由后端保存并写入脱敏审计。');
    } catch (error) {
      setChannelMessage(toConfigError(error));
    } finally {
      setSavingChannels(false);
    }
  }

  if (handlersQuery.isLoading || channelsQuery.isLoading || employeesQuery.isLoading) {
    return <p className="text-sm text-slate-500">正在加载投诉配置…</p>;
  }

  const loadingError =
    handlersQuery.error || channelsQuery.error || employeesQuery.error;
  if (loadingError) {
    return (
      <div role="alert" className="rounded-xl bg-rose-50 p-4 text-sm text-rose-700">
        {toConfigError(loadingError)}
      </div>
    );
  }

  return (
    <div className="grid gap-6 xl:grid-cols-2" data-testid="complaint-config-panel">
      <form className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" onSubmit={saveHandlers}>
        <h2 className="text-base font-semibold text-slate-900">分类处理人</h2>
        <p className="mt-1 text-xs leading-5 text-slate-500">
          选择器显示必要的脱敏员工信息；请求只提交 immutable employeeId。停用、注销或跨组织员工会被后端拒绝。
        </p>
        <div className="mt-4 space-y-4">
          <HandlerSelect
            label="合规安全类处理人"
            value={complianceEmployeeId}
            onChange={setComplianceEmployeeId}
            employees={employees}
          />
          <HandlerSelect
            label="企业培训类处理人"
            value={trainingEmployeeId}
            onChange={setTrainingEmployeeId}
            employees={employees}
          />
        </div>
        {employeesQuery.data && employeesQuery.data.pagination.total > employees.length ? (
          <p className="mt-3 text-xs text-amber-700">
            在职员工超过 100 人；请先在员工管理中确认目标员工，避免使用姓名猜测匹配。
          </p>
        ) : null}
        {handlerMessage ? (
          <p role="status" className="mt-3 text-sm text-slate-700">
            {handlerMessage}
          </p>
        ) : null}
        <button
          type="submit"
          disabled={savingHandlers}
          className="mt-4 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60"
          data-testid="complaint-save-handlers"
        >
          {savingHandlers ? '保存中…' : '保存处理人配置'}
        </button>
      </form>

      <form className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" onSubmit={saveChannels}>
        <h2 className="text-base font-semibold text-slate-900">受控投诉渠道</h2>
        <p className="mt-1 text-xs leading-5 text-slate-500">
          两项均从后端配置下发；空值表示不展示，不回退任何硬编码真实联系方式。
        </p>
        <div className="mt-4 space-y-4">
          <label className="block text-sm text-slate-700">
            <span className="mb-1 block">备用企业微信</span>
            <input
              className={INPUT}
              value={enterpriseWechat}
              maxLength={64}
              onChange={(event) => setEnterpriseWechat(event.target.value)}
            />
          </label>
          <label className="block text-sm text-slate-700">
            <span className="mb-1 block">行政投诉专线</span>
            <input
              className={INPUT}
              value={hotline}
              maxLength={64}
              onChange={(event) => setHotline(event.target.value)}
            />
          </label>
        </div>
        {channelMessage ? (
          <p role="status" className="mt-3 text-sm text-slate-700">
            {channelMessage}
          </p>
        ) : null}
        <button
          type="submit"
          disabled={savingChannels}
          className="mt-4 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60"
          data-testid="complaint-save-channels"
        >
          {savingChannels ? '保存中…' : '保存受控渠道'}
        </button>
      </form>
    </div>
  );
}

interface HandlerSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  employees: AdminEmployeeListResult['items'];
}

function HandlerSelect({ label, value, onChange, employees }: HandlerSelectProps) {
  return (
    <label className="block text-sm text-slate-700">
      <span className="mb-1 block">{label}</span>
      <select
        className={INPUT}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">请选择在职员工</option>
        {employees.map((employee) => (
          <option key={employee.id} value={employee.id}>
            {employee.name} · {employee.phoneMasked} · {employee.deptName || '未分部门'} ·{' '}
            {employee.role} · {maskEmployeeId(employee.id)}
          </option>
        ))}
      </select>
    </label>
  );
}

function toConfigError(error: unknown): string {
  if (error instanceof ApiError) {
    const code = error.code ? ` · ${String(error.code)}` : '';
    return error.status > 0
      ? `${error.message}（HTTP ${error.status}${code}）`
      : error.message;
  }
  return error instanceof Error ? error.message : '投诉配置操作失败。';
}
